import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';

import { SnackbarTemplateType } from '@ninety/platform/models/enums/snackbar-template-type';
import { NotifyService } from '@ninety/ui/legacy/core/services/notify.service';

import { ConsentApiService } from '../integration-consent.service';

import { ConsentViewState } from './auth-models';
import { ConsentActions } from './consent.actions';

function createInitialState(): ConsentViewState {
  return {
    redirectUri: '',
    clientId: null,
    companyName: null,
    companyLogo: null,
    redirectState: null,
    loading: false,
    error: null,
  };
}

@Injectable()
export class ConsentComponentStore extends ComponentStore<ConsentViewState> {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ConsentApiService);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly notifyService = inject(NotifyService);

  constructor() {
    super(createInitialState());
    this.authorizeIntegrationSuccess$();
    this.registerAuthorizeIntegration$();
    this.denyIntegration$();
    this.getCompanyDetails$();
  }

  readonly redirectUri$ = this.select(s => s.redirectUri);
  readonly clientId$ = this.select(s => s.clientId);
  readonly companyName$ = this.select(s => s.companyName);
  readonly companyLogo$ = this.select(s => s.companyLogo);
  readonly redirectState$ = this.select(s => s.redirectState);
  readonly loading$ = this.select(s => s.loading);
  readonly error$ = this.select(s => s.error);

  readonly vm$ = this.select(
    this.redirectUri$,
    this.clientId$,
    this.companyName$,
    this.companyLogo$,
    this.state$,
    this.loading$,
    this.error$,
    (redirectUri, clientId, companyName, companyLogo, redirectState, loading, error) => ({
      redirectUri,
      clientId,
      companyName,
      companyLogo,
      redirectState,
      loading,
      error,
    })
  );

  private registerAuthorizeIntegration$() {
    this.effect<void>(origin$ =>
      origin$.pipe(
        startWith(undefined),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ConsentActions.authorizeIntegration),
            tap(() => this.patchState({ loading: true, error: null })),
            switchMap(action => {
              return this.api.authorizeIntegration(action.authorizeIntegrationParams).pipe(
                map(response => {
                  this.patchState({ loading: false });
                  this.store.dispatch(
                    ConsentActions.authorizeIntegrationSuccess({ redirect_uri: response.redirect_uri })
                  );
                }),
                catchError((err: unknown) => {
                  const error = extractErrorMessage(err);
                  this.patchState({ loading: false, error });
                  this.store.dispatch(ConsentActions.authorizeIntegrationFailure({ error }));
                  return EMPTY;
                })
              );
            })
          )
        )
      )
    )();
  }

  private authorizeIntegrationSuccess$() {
    this.effect(origin$ =>
      origin$.pipe(
        switchMap(() =>
          this.actions$.pipe(
            ofType(ConsentActions.authorizeIntegrationSuccess),
            tap(({ redirect_uri }) => {
              if (redirect_uri) {
                window.location.href = redirect_uri;
              }
            })
          )
        )
      )
    )();
  }

  private readonly denyIntegration$ = this.effect(origin$ =>
    origin$.pipe(
      switchMap(() =>
        this.actions$.pipe(
          ofType(ConsentActions.denyIntegration),
          tap(() => {
            this.dialog.closeAll();
            this.notifyService.notifyWithTemplate(
              SnackbarTemplateType.success,
              { message: 'Authorization Denied' },
              8000
            );
          })
        )
      )
    )
  );

  private getCompanyDetails$() {
    this.effect<void>(origin$ =>
      origin$.pipe(
        startWith(undefined),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ConsentActions.getCompanyDetails),
            tap(() => this.patchState({ loading: true, error: null })),
            switchMap(action => {
              return this.api.getCompanyDetails(action.clientId).pipe(
                tap({
                  next: ({ name, logo_url }) => {
                    this.patchState({ companyName: name, companyLogo: logo_url, loading: false });
                  },
                  error: () => {
                    this.patchState({ companyName: null, companyLogo: null, loading: false });
                  },
                })
              );
            })
          )
        )
      )
    )();
  }
}

function extractErrorMessage(err: unknown): string {
  if (err === null || typeof err !== 'object') return 'Request failed';

  if ('error' in err) {
    const body: unknown = err.error;
    if (body !== null && typeof body === 'object') {
      if ('error' in body && typeof body.error === 'string') return body.error;
      if ('message' in body && typeof body.message === 'string') return body.message;
    }
  }
  return 'message' in err && typeof err.message === 'string' ? err.message : 'Request failed';
}
